import type { ReactNode } from 'react';

import { generateCageNames } from './generateCageNames';

export const MIN_COUNT = 1;
export const MAX_COUNT = 100;

export interface NameField {
    value: string;
    error: string;
}

export interface FormState {
    /** The validated integer count used to drive name generation. */
    count: number;
    /** Raw string value of the count input — allows clearing/backspacing freely. */
    countRaw: string;
    countError: string;
    cageTypeId: number;
    cageTypeError: string;
    fishTypeId: number;
    fishTypeError: string;
    names: NameField[];
    saving: boolean;
    /** A form-wide error: copy of its own, or what a failed save said. */
    systemError: ReactNode;
}

function makeDefaultNames(prefix: string, count: number): NameField[] {
    return generateCageNames(`${prefix} 01`, count).map((value) => ({ value, error: '' }));
}

export function makeInitialState(defaultPrefix: string): FormState {
    return {
        count: 1,
        countRaw: '1',
        countError: '',
        cageTypeId: -1,
        cageTypeError: '',
        fishTypeId: -1,
        fishTypeError: '',
        names: makeDefaultNames(defaultPrefix, 1),
        saving: false,
        systemError: '',
    };
}

/**
 * The form after the count input changes. An invalid count keeps the names as they are while
 * the field is being edited; a valid one regenerates them from the first name.
 */
export function applyCountChange(prev: FormState, raw: string, defaultPrefix: string): FormState {
    const parsed = Number.parseInt(raw, 10);
    const isValid = !Number.isNaN(parsed) && parsed >= MIN_COUNT && parsed <= MAX_COUNT;
    if (!isValid) {
        // Keep existing names unchanged while the field is being edited
        return { ...prev, countRaw: raw, countError: '' };
    }
    const template = prev.names[0]?.value || `${defaultPrefix} 01`;
    return {
        ...prev,
        count: parsed,
        countRaw: raw,
        countError: '',
        names: generateCageNames(template, parsed).map((value, i) => ({
            value,
            error: i === 0 ? (prev.names[0]?.error ?? '') : '',
        })),
    };
}

/** The form after a name changes. Editing the first name renumbers the rest from it. */
export function applyNameChange(prev: FormState, index: number, value: string): FormState {
    if (index === 0) {
        const generated = generateCageNames(value, prev.count);
        return {
            ...prev,
            names: prev.names.map((field, i) => ({
                value: i === 0 ? value : generated[i],
                error: '',
            })),
        };
    }
    return {
        ...prev,
        names: prev.names.map((field, i) => (i === index ? { value, error: '' } : field)),
    };
}
